import React from 'react';
import { Button, Input, InputNumber, Modal, Space } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { useDroppable } from '@dnd-kit/core';
import type { LaneCard } from '../../CommissionManager/types';
import { LaneCardDisplay } from '../../CommissionManager/components/LaneCardDisplay';
import type { TaxBandRule } from '../taxTypes';

interface TaxRuleBlockProps {
  rule: TaxBandRule;
  taxCodeError?: string;
  onTaxCodeBlur?: () => void;
  onRuleChange: (rule: TaxBandRule) => void;
  onRemoveRule: () => void;
  onCardRemove: (lane: 'include' | 'exclude', cardId: string) => void;
  onCardConfigure: (lane: 'include' | 'exclude', card: LaneCard) => void;
}

export const TaxRuleBlock: React.FC<TaxRuleBlockProps> = ({
  rule,
  taxCodeError,
  onTaxCodeBlur,
  onRuleChange,
  onRemoveRule,
  onCardRemove,
  onCardConfigure,
}) => {
  const updateRule = (patch: Partial<TaxBandRule>) => {
    onRuleChange({ ...rule, ...patch });
  };

  const { setNodeRef: setIncludeRef, isOver: isOverInclude } = useDroppable({
    id: `tax-include-${rule.id}`,
    data: { lane: 'include', ruleId: rule.id },
  });

  const { setNodeRef: setExcludeRef, isOver: isOverExclude } = useDroppable({
    id: `tax-exclude-${rule.id}`,
    data: { lane: 'exclude', ruleId: rule.id },
  });

  return (
    <div className="commission-rule-block tax-rule-block">
      <div className="commission-rule-header tax-rule-header-row">
        <Input
          placeholder="Rule name (required)"
          value={rule.name}
          onChange={(e) => updateRule({ name: e.target.value })}
          style={{ flex: 1, maxWidth: 280 }}
        />
        <span className="tax-rule-applies-label">applies at</span>
        <Space.Compact style={{ width: 120 }}>
        <InputNumber
          min={0}
          max={100}
          value={rule.taxRatePercent ?? null}
          onChange={(v) =>
            updateRule({ taxRatePercent: v == null || typeof v !== 'number' ? 0 : v })
          }
          style={{ width: '100%' }}
        />
          <Space.Addon>{'%'}</Space.Addon>
        </Space.Compact>
        <Button
          type="text"
          danger
          size="small"
          icon={<DeleteOutlined />}
          style={{ marginLeft: 'auto' }}
          onClick={() => {
            Modal.confirm({
              title: 'Delete rule',
              content: 'Remove this rule?',
              okText: 'Delete',
              okType: 'danger',
              cancelText: 'Cancel',
              onOk: onRemoveRule,
            });
          }}
        />
      </div>

      <div className="commission-rule-lanes commission-rule-lanes--muted">
        <div className="commission-lane commission-lane--panel">
          <div className="commission-lane-header">
            <span className="commission-lane-title">Include</span>
            <span className="commission-lane-sub">
              Select the products this tax applies to. All selected Include rules must match.
            </span>
          </div>
          <div
            ref={setIncludeRef}
            className={`commission-lane-dropzone ${isOverInclude ? 'commission-lane-dropzone-over' : ''}`}
          >
            {rule.include.map((card) => (
              <LaneCardDisplay
                key={card.id}
                card={card}
                lane="include"
                onRemove={() => {
                  if (!card.isMirror) onCardRemove('include', card.id);
                }}
                onConfigure={() => {
                  if (!card.isMirror) onCardConfigure('include', card);
                }}
              />
            ))}
          </div>
        </div>

        <div className="commission-lane commission-lane--panel">
          <div className="commission-lane-header">
            <span className="commission-lane-title">Exclude</span>
            <span className="commission-lane-sub">
              Products that match an Exclude rule will not have this tax applied.
            </span>
          </div>
          <div
            ref={setExcludeRef}
            className={`commission-lane-dropzone ${isOverExclude ? 'commission-lane-dropzone-over' : ''}`}
          >
            {rule.exclude.map((card) => (
              <LaneCardDisplay
                key={card.id}
                card={card}
                lane="exclude"
                onRemove={() => {
                  if (!card.isMirror) onCardRemove('exclude', card.id);
                }}
                onConfigure={() => {
                  if (!card.isMirror) onCardConfigure('exclude', card);
                }}
              />
            ))}
          </div>
        </div>
      </div>

      <div className="tax-rule-footer-row">
        <div className="tax-rule-code-field">
          <span className="tax-rule-code-label tax-required-label">Tax Code</span>
          <div className="tax-rule-code-input-col">
            <Input
              placeholder="4-7 digits"
              maxLength={7}
              value={rule.taxCode}
              onChange={(e) => updateRule({ taxCode: e.target.value.replace(/\D/g, '') })}
              onBlur={() => onTaxCodeBlur?.()}
              status={taxCodeError ? 'error' : undefined}
              style={{ width: 120 }}
            />
            {taxCodeError ? <div className="tax-rule-code-error">{taxCodeError}</div> : null}
          </div>
        </div>
      </div>
    </div>
  );
};